// Practice sets for the Sanskrit games, the same idea as the Prakrit ones (src/prakrit/lib/practice/bank.ts): for every game,
// level and choice of topics a bank of up to BANK_TARGET different exercises, in an order that stays the same (set ७
// is set ७ for everyone, so the score kept per set means something), cut into sets of SET_SIZE. The bank keeps only
// the seed of each exercise and makes the question again from it when it is asked. The Sanskrit course has no server
// data, so the bank is built in the browser, once per page load, and kept in memory.

import type { GameDef, PracticeQuestion } from '@/sanskrit/lib/practice/generators'

import { hashString, mulberry32, seededShuffle } from '@/sanskrit/lib/random'
import { scopeOf, topicsForGame } from '@/sanskrit/lib/practice/topics'

/** The exercises one bank holds at most. */
export const BANK_TARGET = 500

/** Exercises in one set. */
export const SET_SIZE = 10

/** Seeds tried before a bank stops looking; a game with little content stops sooner (MISS_LIMIT). */
const MAX_TRIES = 6000

/** A bank stops when this many seeds in a row brought nothing new and it already tried MIN_TRIES. */
const MISS_LIMIT = 700
const MIN_TRIES = 1200

export interface SanskritBank {
    key: string
    seeds: number[]
    total: number
    sets: number
}

const banks = new Map<string, SanskritBank>()

export const bankKey = (game: GameDef, level: number, topics: string[]): string => [game.id, level, [...topicsForGame(game.id, topics)].sort().join(',')].join('|')

const seedAt = (base: number, i: number): number => (((base ^ Math.imul(i + 1, 2654435761)) >>> 0) % 2147483646) + 1

/** The question one seed makes, or null when it cannot be asked (too few options). */
export const questionAt = (game: GameDef, level: number, topics: string[], seed: number): PracticeQuestion | null => {
    try {
        const q = game.generate(mulberry32(seed), level, { scope: scopeOf(game.id, topics) })

        if (q.kind === 'mcq' && (q.options.length < 2 || q.answer < 0)) return null
        if (q.kind === 'listen' && q.options.length < 2) return null
        if (q.kind === 'reorder' && q.items.length < 2) return null

        return q
    }
    catch (error) {
        console.error('[sanskrit practice bank] an exercise failed to build', game.id, error instanceof Error ? error.message : error)

        return null
    }
}

const build = (game: GameDef, level: number, topics: string[], key: string): SanskritBank => {
    const base = hashString(`bank|${key}`)
    const seen = new Set<string>()
    const seeds: number[] = []
    let misses = 0

    for (let i = 0; i < MAX_TRIES && seeds.length < BANK_TARGET; i += 1) {
        const seed = seedAt(base, i)
        const q = questionAt(game, level, topics, seed)

        if (q && !seen.has(q.key)) {
            seen.add(q.key)
            seeds.push(seed)
            misses = 0
        }
        else {
            misses += 1

            if (misses >= MISS_LIMIT && i >= MIN_TRIES) break
        }
    }

    return { key, seeds, total: seeds.length, sets: Math.ceil(seeds.length / SET_SIZE) }
}

/** The bank for a game, level and topics, built once per page load. */
export const getBank = (game: GameDef, level: number, topics: string[]): SanskritBank => {
    const key = bankKey(game, level, topics)
    const cached = banks.get(key)

    if (cached) return cached

    const bank = build(game, level, topics, key)

    banks.set(key, bank)

    return bank
}

/** How many exercises set `set` (1-based) has: SET_SIZE, or fewer for the last one. */
export const setLength = (bank: SanskritBank, set: number): number => Math.max(0, Math.min(SET_SIZE, bank.seeds.length - (set - 1) * SET_SIZE))

/** The questions of set `set`, in a new order for every play (`order`). */
export const setQuestions = (bank: SanskritBank, game: GameDef, level: number, topics: string[], set: number, order: number): PracticeQuestion[] => {
    const size = setLength(bank, set)
    const seeds = bank.seeds.slice((set - 1) * SET_SIZE, (set - 1) * SET_SIZE + size)

    return seededShuffle(seeds, order >>> 0)
        .map(seed => questionAt(game, level, topics, seed))
        .filter((q): q is PracticeQuestion => q !== null)
}
